<template>
    <div>
        <Loader wait-text="Loading Multi-Factor Authentication status..." v-if="isLoading"/>
        <div v-else>
            <div v-if="!status.is_setup">
                <div class="alert alert-warning">
                    Multi-Factor authentication (MFA) is not setup for this account.
                    Setting up Multi-Factor authentication will strengthen your account security by adding an additional
                    step to be performed after successful login.
                </div>

                <button class="btn btn-primary" @click="showModal = true">Setup Multi-Factor Authentication</button>
            </div>
            <div v-else>
                <div class="alert alert-warning">
                    This account has Multi-Factor Authentication (MFA) setup.
                    <strong v-if="status.is_required">
                        MFA is required by the administrator for this account and as such it cannot be removed.
                    </strong>
                </div>

                <button class="btn btn-danger" v-if="!status.is_required" @click="deleteMfaSetup">
                    Remove Multi-Factor Authentication
                </button>
            </div>
        </div>
    </div>

    <Modal :show="showModal" size="xl" :header="true" @close="showModal = false">
        <template #title>
            Setup Multi-Factor Authentication
        </template>

        <MfaSetupForm v-if="showModal"
                      :user="user"
                      :is-loading="isVerifying"
                      :error-message="errorMessage"
                      @confirmed="handleSetupConfirmed"/>
    </Modal>
</template>

<script>
import { onMounted, ref } from "vue";

import { getMfaStatus, removeMfa, setupMfa } from "@/api/user";
import { useLoading } from "@/hooks/use-loading";

import Loader from "@/components/Loader.vue";
import Modal from "@/components/Modal";
import MfaSetupForm from "@/pages/common/MfaSetupForm";
import { confirm } from "@/lib/sweetalert";

export default {
    name: "MfaChange",
    components: { MfaSetupForm, Modal, Loader },
    props: {
        user: Object,
    },
    setup() {
        const status = ref({
            is_setup: false,
            is_required: false
        });

        const showModal = ref(false);

        const errorMessage = ref('');

        const { isLoading, startLoading, stopLoading } = useLoading(true);
        const {
            isLoading: isVerifying,
            startLoading: startVerification,
            stopLoading: stopVerification
        } = useLoading(false);

        onMounted(() => loadStatus());

        const loadStatus = async () => {
            startLoading();

            try {
                status.value = (await getMfaStatus()).data.result;
            } catch (e) {
                console.error(e);
            }

            stopLoading();
        };

        const deleteMfaSetup = async () => {
            const { isConfirmed } = await confirm({
                text: 'If you remove MFA setup you will weaken your account security. You will need to setup MFA again after this.'
            });

            if (!isConfirmed) {
                return;
            }

            startLoading();
            try {
                await removeMfa();
                await loadStatus();
            } catch (e) {
                console.error(e);
            }

            stopLoading();
        };

        const handleSetupConfirmed = async ({ code, type }) => {
            startVerification();
            try {
                await setupMfa({ code, type });
                showModal.value = false;
                await loadStatus();
            } catch (e) {
                errorMessage.value = e?.response?.data?.message ?? e.message;
            }
            stopVerification();
        };

        return {
            status,
            showModal,
            isLoading,
            isVerifying,
            errorMessage,
            deleteMfaSetup,
            handleSetupConfirmed
        };
    }
}
</script>